import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import {
  AttachmentIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { FaFileCsv } from 'react-icons/fa';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';

import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { subQuestReportActions } from 'store/modules/subQuestReport/subQuest-report-slice';
import { importBulkFileSubmissionsAction } from 'store/modules/subQuestReport/subQuest-report-action';
import {
  IDLE,
  LOADING,
  SUCCEEDED,
  FAILED,
} from 'utils/constants/reducerStatus';
import previewColumns from 'utils/columns/fileSubmissionImportColumns';
import useCsvFileInput from 'hooks/useCsvFileInput';

function ImportFileSessionsMd() {
  const dispatch = useDispatch();

  // Color mode values for theme support
  const helperTextColor = useColorModeValue('gray', '#c0bdbd');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const fileDisplayBg = useColorModeValue('green.50', 'green.900');
  const fileDisplayBorder = useColorModeValue('green.200', 'green.600');

  const importSubmissionSessions = useSelector(
    (state) => state.subQuestReport.importSubmissionSessions
  );

  const {
    fileInputRef,
    selectedFile,
    fileName,
    fileError,
    parsedData,
    parseErrors,
    isParsingCsv,
    setFileError,
    handleFileInputChange,
    handleAttachFile,
    handleRemoveFile,
  } = useCsvFileInput();

  const [showPreview, setShowPreview] = useState(true);
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [importStatus, setImportStatus] = useState(IDLE);

  /**
   * Handles confirm button click
   * Opens confirmation modal if data is valid
   */
  const handleConfirmImport = () => {
    if (!selectedFile || parsedData.length === 0) {
      setFileError('Please upload a valid CSV file with data');
      return;
    }

    if (parseErrors.length > 0) {
      setFileError('Please fix CSV errors before importing');
      return;
    }

    setShowImportConfirmation(true);
    setImportStatus(IDLE);
  };

  /**
   * Handles confirmation modal proceed action
   * Transforms parsed CSV rows into API request records and dispatches the import action.
   * FileSubmissionSessionId is used as the lookup key (mirrors UpdateFileSubmissionSession).
   * Context-only export columns (SubQuestName, UserName, Code, etc.) are excluded from the payload.
   */
  const handleProceedImport = async () => {
    setImportStatus(LOADING);

    try {
      // Map CSV rows to the API request shape using session ID as the key
      const records = parsedData.map((row) => ({
        fileSubmissionSessionId: parseInt(row.FileSubmissionSessionId, 10),
        score: row.Score !== '' ? parseFloat(row.Score) : null,
        remarks: row.Remarks || null,
        showRemarks: row.ShowRemarks?.toLowerCase() === 'true',
      }));

      await dispatch(
        importBulkFileSubmissionsAction({
          subQuestId: importSubmissionSessions.subQuestId,
          records,
        })
      ).unwrap();

      setImportStatus(SUCCEEDED);
    } catch {
      setImportStatus(FAILED);
    }
  };

  /**
   * Handles confirmation modal close/cancel
   * Resets import confirmation state
   */
  const handleCancelImportConfirmation = () => {
    setShowImportConfirmation(false);
    setImportStatus(IDLE);
  };

  /**
   * Handles modal close
   * Resets all state when closing the modal
   */
  const handleCloseModal = () => {
    dispatch(
      subQuestReportActions.setImportSubmissionSessions({ modal: false })
    );

    handleRemoveFile();
    setShowPreview(true);
  };

  /**
   * Handles result acknowledgement after import completes
   * Closes confirmation modal and main modal if successful
   */
  const handleImportResult = () => {
    setShowImportConfirmation(false);
    setImportStatus(IDLE);

    if (importStatus === SUCCEEDED) {
      handleRemoveFile();
      handleCloseModal();
    }
  };

  const getImportConfirmationMessage = () => {
    return `Are you sure you want to import ${parsedData.length} file submission record(s)? This will add the records to the system.`;
  };

  /**
   * Gets success message after import
   * Shows updated vs total count and flags any users without an existing session
   */
  const getImportSuccessMessage = () => {
    const { result } = importSubmissionSessions;
    if (!result) return 'Import completed.';
    const notFoundNote =
      result.notFoundSessionIds?.length > 0
        ? ` (${result.notFoundSessionIds.length} session(s) not found)`
        : '';
    return `Successfully updated ${result.updatedCount} of ${result.totalRecords} record(s).${notFoundNote}`;
  };

  return (
    <>
      {/* Main Import Modal */}
      <Modal
        isOpen={importSubmissionSessions.modal}
        onClose={handleCloseModal}
        size={selectedFile ? '6xl' : '2xl'}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>Import File Submissions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* IMPORT FILE SECTION */}
            <Flex direction="column" pb={6}>
              <Text fontWeight="bold">IMPORT CSV FILE</Text>

              {/* Instructions */}
              <Text fontSize="sm" color={helperTextColor}>
                Upload the exported file submissions CSV. Editable columns:
                <Text as="span" fontWeight={500} ml={1}>
                  Score, Remarks, ShowRemarks.
                </Text>
              </Text>

              {/* File Upload Button */}
              {!selectedFile && (
                <Box>
                  <Button
                    mt={2}
                    leftIcon={<AttachmentIcon />}
                    colorScheme="red"
                    onClick={handleAttachFile}
                    w="100%"
                  >
                    Select CSV File
                  </Button>
                  <Input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    accept=".csv"
                    hidden
                  />
                </Box>
              )}

              {/* Selected File Display */}
              {selectedFile && (
                <Flex
                  gap={3}
                  w="100%"
                  p={3}
                  bg={fileDisplayBg}
                  borderRadius="md"
                  border="1px"
                  borderColor={fileDisplayBorder}
                  align="center"
                >
                  <Icon as={FaFileCsv} color="green.500" boxSize={5} />
                  <VStack align="start" flex={1} spacing={0}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {fileName}
                    </Text>
                    <Text fontSize="xs" color={helperTextColor}>
                      {(selectedFile.size / 1024).toFixed(2)} KB
                      {parsedData.length > 0 &&
                        ` • ${parsedData.length} records`}
                    </Text>
                  </VStack>
                  <Button
                    colorScheme="red"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                  >
                    <CloseIcon />
                  </Button>
                </Flex>
              )}

              {/* File Error Display */}
              {fileError && (
                <Text fontSize="sm" color="red.500">
                  {fileError}
                </Text>
              )}

              {/* Parse Errors Display */}
              {parseErrors.length > 0 && (
                <Box
                  p={3}
                  bg="red.50"
                  borderRadius="md"
                  border="1px"
                  borderColor="red.200"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="red.700"
                    mb={2}
                  >
                    CSV Header Errors:
                  </Text>
                  <VStack align="start" spacing={1}>
                    {parseErrors.map((error, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Text key={index} fontSize="sm" color="red.600">
                        • {error}
                      </Text>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Parsing Loading State */}
              {isParsingCsv && (
                <Box minHeight="60px">
                  <LoadingSpinner size="sm" message="Parsing CSV file..." />
                </Box>
              )}
            </Flex>

            {/* PREVIEW SECTION */}
            {parsedData.length > 0 && (
              <>
                <Divider my={6} />
                <Flex direction="column">
                  {/* Preview Header with Toggle */}
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center" gap={3}>
                      <Text fontWeight="bold">PREVIEW</Text>
                      <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                        {parsedData.length} records
                      </Badge>
                    </Flex>
                    <IconButton
                      icon={
                        showPreview ? <ChevronUpIcon /> : <ChevronDownIcon />
                      }
                      onClick={() => setShowPreview(!showPreview)}
                      size="sm"
                      variant="ghost"
                      aria-label={showPreview ? 'Hide preview' : 'Show preview'}
                    />
                  </Flex>

                  {/* Collapsible Preview Content */}
                  <Collapse in={showPreview} animateOpacity>
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={borderColor}
                      overflow="hidden"
                    >
                      <DataTable
                        columns={previewColumns}
                        data={parsedData}
                        pagination
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent={
                          <Text p={4} color={helperTextColor}>
                            No data to preview
                          </Text>
                        }
                      />
                    </Box>
                  </Collapse>
                </Flex>
              </>
            )}
          </ModalBody>

          {/* FOOTER */}
          <ModalFooter>
            <Button
              colorScheme="gray"
              mr={3}
              px={10}
              onClick={handleCloseModal}
            >
              Close
            </Button>
            <Button
              colorScheme="red"
              px={10}
              onClick={handleConfirmImport}
              isDisabled={
                !selectedFile ||
                parsedData.length === 0 ||
                parseErrors.length > 0
              }
              isLoading={importStatus === LOADING}
            >
              Confirm Import
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Import Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showImportConfirmation}
        onClose={handleCancelImportConfirmation}
        onConfirm={handleProceedImport}
        onSubmitResult={handleImportResult}
        status={importStatus}
        description={getImportConfirmationMessage()}
        successMessage={getImportSuccessMessage()}
        error={null}
      />
    </>
  );
}

export default ImportFileSessionsMd;
